import { Fragment, useState } from 'react';
import type { FC, ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import PrivateContent from 'app/common/PrivateContent';
import ResetedButton from 'app/components/Button/ResetedButton';
import blacklistIcon from 'app/icons/blacklist.svg';
import watchlistIcon from 'app/icons/watchlist.svg';
import { getBlacklistMatchSituation } from 'app/utils/match';
import Icon from 'app/components/Icon';
import Tooltip from 'app/components/Tooltip';
import OpponentAvatar from 'app/components/OpponentAvatar';
import { getBlacklistPlayerTeamAsOpponent } from 'app/pages/BlacklistPage/utils';
import type { StringBlacklistStatus } from 'app/pages/BlacklistPage/types';
import { partitionArray } from 'app/utils/array';
import PlayerHistoryModal from 'app/pages/BlacklistPage/PlayerHistoryModal';

import style from './style.scss';

interface Props {
  blacklistedPlayers?: BlacklistPlayer[];
  className?: string;
  matchStatus: MatchStatus;
}

const getPlayerListAsElement = (
  players: BlacklistPlayer[],
  list: StringBlacklistStatus,
  t: TFunction
): ReactNode => (
  <div className={style.playerList}>
    {players.map<ReactNode>((player, index) => (
      <Fragment key={player.id}>
        <span className={style.listPlayerItem}>
          {player.current_team && (
            <OpponentAvatar opponent={getBlacklistPlayerTeamAsOpponent(player.current_team)} />
          )}
          {player.name}
        </span>
        {index < players.length - 1 && ','}
        &nbsp;
      </Fragment>
    ))}
    {players.length === 1
      ? t('events-match-blacklist-tooltip-is')
      : t('events-match-blacklist-tooltip-are')}
    {t('events-match-blacklist-tooltip-on')}
    {t(`blacklist-status-${list}`).toLowerCase()}.
  </div>
);

const getTooltipContent = (players: BlacklistPlayer[], t: TFunction): ReactNode => {
  const playersByList = partitionArray(players, player => player.status || 'none');

  return (
    <div className={style.tooltipContent}>
      {playersByList.blacklist &&
        playersByList.blacklist?.length > 0 &&
        getPlayerListAsElement(playersByList.blacklist, 'blacklist', t)}
      {playersByList.watchlist &&
        playersByList.watchlist?.length > 0 &&
        getPlayerListAsElement(playersByList.watchlist, 'watchlist', t)}
    </div>
  );
};

const BlacklistIndicator: FC<Props> = ({ blacklistedPlayers, className, matchStatus }) => {
  const [t] = useTranslation();

  const [showBlacklistInfo, setShowBlacklistInfo] = useState(false);

  const { blacklist: hasBlacklistedPlayers, watchlist: hasWatchlistedPlayers } =
    getBlacklistMatchSituation(blacklistedPlayers);

  if (
    blacklistedPlayers &&
    (hasBlacklistedPlayers || hasWatchlistedPlayers) &&
    ['set_up', 'pre_match', 'not_booked'].includes(matchStatus)
  ) {
    return (
      <PrivateContent feature="BLACKLIST">
        <>
          <Tooltip content={getTooltipContent(blacklistedPlayers, t)}>
            <ResetedButton
              onClick={() => setShowBlacklistInfo(true)}
              className={style.blacklistModalButton}
            >
              {hasBlacklistedPlayers && (
                <div className={classNames(style.blacklistIndicator, className)}>
                  <Icon href={blacklistIcon} className={style.blacklistIcon} />
                </div>
              )}
              {!hasBlacklistedPlayers && hasWatchlistedPlayers && (
                <div className={classNames(style.watchlistIndicator, className)}>
                  <Icon href={watchlistIcon} className={style.blacklistIcon} />
                </div>
              )}
            </ResetedButton>
          </Tooltip>
          {showBlacklistInfo && (
            <PlayerHistoryModal
              playerItems={blacklistedPlayers || []}
              onClose={() => setShowBlacklistInfo(false)}
            />
          )}
        </>
      </PrivateContent>
    );
  }

  return null;
};

BlacklistIndicator.displayName = 'BlacklistIndicator';

export default BlacklistIndicator;
